import React, { useEffect, useCallback } from 'react';
import dayjs from 'dayjs';
import { each, isEmpty } from 'lodash';
import { formatDate, removeEmpty, withReqHookDefaultOptions } from 'helper';
import { req, useApiGet, useApiLoading } from 'react-reqq-lite';
import { sortList } from 'partial/components/Table';
import { useDelayedApiLoading } from 'partial/hooks';
import { useActiveAccount } from 'modules/auth/hooks';
import { useGetProfile } from 'core-modules/profile/hooks';
import { PROFILE } from 'core-modules/profile/constants';
import { transformCompareTable, transformCompareChart } from './transformers';
import * as actions from './actions';
import * as c from './constants';

export const useCountCard = (name, filter) => {
  const isLoading = useDelayedApiLoading(`${c.COUNT_CARD}/${name}`, 'get');
  const { value } = useApiGet(`${c.COUNT_CARD}/${name}`, {});
  React.useEffect(() => {
    const action = actions.getCountCard[name];
    if (typeof action === 'function') action(filter);
  }, [name, filter]);
  return [isLoading, value];
};

export const useTransactionHistogram = (filter) => {
  const isLoading = useDelayedApiLoading(
    `${c.DASHBOARD_HISTOGRAM}/transaction`,
    'get'
  );
  const { list } = useApiGet(`${c.DASHBOARD_HISTOGRAM}/transaction`, []);
  React.useEffect(() => {
    const { type, from: dFrom, to: dTo, ...rest } = filter;
    const params = {};
    if (dFrom) params.date_from = dFrom;
    if (dTo) params.date_to = dTo;
    if (type) params.type = type;
    actions.getDashboardHistogram(
      {
        ...params,
        ...rest,
      },
      'transaction'
    );
  }, [filter]);
  const { table, chart } = React.useMemo(() => {
    const tableData = (list || []).map((x) => ({
      id: x.label,
      label: x.label,
      count: x.transaction,
      transaction: x.transaction,
      amount: x.amount,
    }));
    const chartData = (list || []).map((x) => ({
      id: x.label,
      label: x.label,
      count: x.transaction,
      transaction: x.transaction,
      amount: x.amount,
    }));
    return {
      table: tableData,
      chart: chartData,
    };
  }, [list]);
  return [isLoading, table, chart, ''];
};

export const useAmountHistogram = (filter) => {
  const isLoading = useDelayedApiLoading(
    `${c.DASHBOARD_HISTOGRAM}/amount`,
    'get'
  );
  const { list } = useApiGet(`${c.DASHBOARD_HISTOGRAM}/amount`, {});
  React.useEffect(() => {
    const { type, from: date_from, to: date_to } = filter;
    actions.getDashboardHistogram(
      {
        'type[]': 'CREDIT',
        type,
        date_from,
        date_to,
      },
      'amount'
    );
  }, [filter]);
  const { table, chart } = React.useMemo(() => {
    const tableData = (list || []).map((x) => ({
      id: x.label,
      name: x.label,
      transaction: x.transaction,
      amount: x.amount,
    }));
    const chartData = (list || []).map((x) => ({
      id: x.label,
      title: x.label,
      count: x.transaction,
    }));
    return {
      table: tableData,
      chart: chartData,
    };
  }, [list]);
  return [isLoading, table, chart, ''];
};

export const useTopMerchantScheme = (filter, meta) => {
  const isLoading = useDelayedApiLoading(
    `${c.DASHBOARD_SCHEME}/top_merchants`,
    'get'
  );
  const { list } = useApiGet(`${c.DASHBOARD_SCHEME}/top_merchants`, {});
  React.useEffect(() => {
    actions.getDashboardScheme(
      {
        // 'type[]': 'BET',
        'entry[]': 'CREDIT',
        'group[]': 'details.game_uuid.keyword',
      },
      'top_merchants'
    );
  }, [filter]);
  const { table, chart } = React.useMemo(() => {
    const formatted = [];
    const chartData = [];
    (list || []).forEach((x) => {
      formatted.push({
        id: x.id,
        label: x.label,
        count: x.count,
        amount: x.amount,
      });
      chartData.push({
        id: x.id,
        title: x.label,
        count: x.count,
      });
    });
    const tableData = sortList(formatted, meta.sort);
    return { table: tableData, chart: chartData };
  }, [list, meta.sort]);

  return [isLoading, table, chart, ''];
};

export const useTransactionScheme = (filter, meta) => {
  const isLoading = useDelayedApiLoading(
    `${c.DASHBOARD_SCHEME}/transaction`,
    'get'
  );
  const { list } = useApiGet(`${c.DASHBOARD_SCHEME}/transaction`, {});
  React.useEffect(() => {
    const { type, from: dFrom, to: dTo, ...rest } = filter;
    const params = {};
    if (dFrom) params.date_from = dFrom;
    if (dTo) params.date_to = dTo;
    if (type) params.type = type;
    actions.getDashboardScheme(
      {
        type,
        ...params,
        'group[]': 'transaction',
        ...rest,
      },
      'transaction'
    );
  }, [filter]);
  const { table, chart } = React.useMemo(() => {
    const tableData = [];
    const chartData = [];
    (list || []).forEach((x) => {
      tableData.push({
        id: x.id,
        label: x.label,
        count: x.transaction,
        transaction: x.transaction,
        amount: x.amount,
      });
      chartData.push({
        id: x.id,
        title: x.label,
        count: x.transaction,
      });
    });
    const sortedTable = sortList(tableData, meta.sort);
    return { table: sortedTable, chart: chartData };
  }, [list, meta.sort]);
  return [isLoading, table, chart, ''];
};

export const useTransactionList = (filter) => {
  const isLoading = useDelayedApiLoading(c.TRANSACTION_LIST, 'get');
  const list = useApiGet(c.TRANSACTION_LIST, []);
  const pager = useApiGet(c.TRANSACTION_PAGER, {});
  const [activeAccount] = useActiveAccount();
  React.useEffect(() => {
    actions.listTransaction(removeEmpty(filter));
    // if (activeAccount?.role === 'organization') {
    //   actions.listTransaction('organization', removeEmpty(filter));
    // }
    // if (activeAccount?.role === 'biller') {
    //   actions.listTransaction('biller', removeEmpty(filter));
    // }
  }, [filter, activeAccount]);
  return [isLoading, list, pager];
};

export const useSelectedTransaction = ({ id }) => {
  const isLoading = useApiLoading(`${c.TRANSACTION_SELECTED}`, 'get');
  const data = useApiGet(`${c.TRANSACTION_SELECTED}`, {});
  const fetchData = useCallback(() => {
    if (!id) return;
    actions.selectedBillerTransaction(id);
  }, [id]);

  useEffect(fetchData, [fetchData]);
  return [isLoading, data, fetchData];
};

export const useRequesetDisputeTransaction = withReqHookDefaultOptions(
  (options) => {
    const isLoading = useApiLoading(c.TRANSACTION_DISPUTE_REQUEST, 'post');
    // eslint-disable-next-line
    const requestDisputeTransaction = useCallback(
      actions.requestDisputeTransaction(options.onSuccess, options.onError),
      [options.onSuccess, options.onError]
    );

    return {
      requestDisputeTransaction,
      isLoading,
    };
  }
);

export const useTransactionTotal = (filter) => {
  const data = useApiGet(c.TRANSACTION_TOTAL, []);

  React.useEffect(() => {
    actions.transactionTotal(removeEmpty(filter));
  }, [data, filter]);
  return [data];
};

const MAP_GROUP = {
  day: 'DAILY',
  month: 'MONTHLY',
  year: 'YEARLY',
};

const MAP_SERIES_FORMAT = {
  day: 'MM/DD/YYYY',
  month: 'MMM YYYY',
  year: 'YYYY',
};

const formatIndex = (key, value) =>
  ({
    day: formatDate(value, 'DD/MM/YYYY'),
    month: formatDate(value, 'MMMM'),
    year: formatDate(value, 'YYYY'),
  }[key]);

export const useCompare = (mode, items, chartValueKey) => {
  const [isLoading] = React.useState(false);
  const data = useApiGet(c.TRANSACTION_COMPARE, {});
  React.useEffect(() => {
    const params = {};
    params.type = MAP_GROUP[mode];
    items.forEach((item, i) => {
      params[`compare[${i}][date_from]`] = dayjs(item)
        .startOf(mode)
        .format('YYYY-MM-DD 00:00:00');
      params[`compare[${i}][date_to]`] = dayjs(item)
        .endOf(mode)
        .format('YYYY-MM-DD 23:59:59');
    });
    req.get({
      key: c.TRANSACTION_COMPARE,
      url: '/wallet/v1/merchant/transactions/compare',
      params,
      onSuccess: (res, state) => {
        req.set(c.TRANSACTION_COMPARE, {
          ...(state[c.TRANSACTION_COMPARE] || {}),
          table: transformCompareTable(mode, items)(res?.response),
          chart: transformCompareChart(mode, items)(res?.response),
        });
      },
    });
  }, [mode, items]);
  const chart = React.useMemo(() => {
    const series = [];
    const ids = {};
    items.forEach((item) => {
      ids[c.buildId(item, mode)] = item;
    });
    each(Object.keys(ids).sort(), (value, i) => {
      const index = formatIndex(mode, value);
      if (!isEmpty(data?.chart) && data?.chart[i]) {
        series.push({
          name: dayjs(ids[value]).format(MAP_SERIES_FORMAT[mode]),
          data: (data?.chart[i][index] || []).map((x) => x[chartValueKey]),
        });
      }
    });
    return series;
  }, [data, items, mode, chartValueKey]);
  return [isLoading, data, chart];
};

// BILLER
export const useBillerCountCards = (name) => {
  const [profile] = useGetProfile();
  const isLoading = useApiLoading(c.BILLER_COUNT_CARDS, 'get');
  const value = useApiGet(c.BILLER_COUNT_CARDS, {});
  React.useEffect(() => {
    if (profile?.biller_id) {
      actions.billerCountCards(profile?.biller_id);
    }
  }, [profile]);
  return [isLoading, value[name]];
};

export const useBillerTransactionHistogram = (params) => {
  const [profile] = useGetProfile();
  const isLoading = useApiLoading(c.BILLER_TRANSACTION, 'get');
  const data = useApiGet(c.BILLER_TRANSACTION, []);
  React.useEffect(() => {
    if (profile?.biller_id) {
      actions.getBillerTransactionHistogram(profile?.biller_id, params);
    }
  }, [profile, params]);
  const { table, chart } = React.useMemo(() => {
    const tableData = [];
    const chartData = [];
    data?.forEach((x) => {
      tableData.push({
        id: x.id,
        label: x.label,
        amount: x.amount,
        count: x.count,
      });
      chartData.push({
        id: x.id,
        label: x.label,
        count: x.count,
      });
    });
    return { table: tableData, chart: chartData };
  }, [data]);
  return [isLoading, table, chart];
};

export const useBillerAmtHistogram = (params) => {
  const [profile] = useGetProfile();
  const isLoading = useApiLoading(c.BILLER_BY_AMOUNT, 'get');
  const data = useApiGet(c.BILLER_BY_AMOUNT, []);
  React.useEffect(() => {
    if (profile?.biller_id) {
      actions.getBillerAmtHistogram(profile?.biller_id, params);
    }
  }, [profile, params]);
  const { table, chart } = React.useMemo(() => {
    const tableData = [];
    const chartData = [];
    data?.forEach((x) => {
      tableData.push({
        id: x.id,
        label: x.label,
        amount: x.amount,
        amount_human: x.amount_human,
      });
      chartData.push({
        id: x.id,
        label: x.label,
        count: x.amount,
      });
    });
    return { table: tableData, chart: chartData };
  }, [data]);
  return [isLoading, table, chart];
};

export const useBillerComparativeTransactions = (params) => {
  const profile = useApiGet(PROFILE, {});
  const isLoading = useApiLoading(c.BILLER_COMPARATIVE_TRANSACTION, 'get');
  const data = useApiGet(c.BILLER_COMPARATIVE_TRANSACTION, {});
  useEffect(() => {
    if (profile?.biller_id && params?.year_start && params?.year_end) {
      actions.billerComparativeTransaction(profile?.biller_id, params);
    }
  }, [params, profile]);
  return [isLoading, data];
};

// ORGANIZATION
export const useOrganizationCountCards = (name) => {
  const profile = useApiGet(PROFILE, {});
  const isLoading = useApiLoading(c.ORGANIZATION_COUNT_CARDS, 'get');
  const value = useApiGet(c.ORGANIZATION_COUNT_CARDS, {});
  useEffect(() => {
    if (profile?.organization_id) {
      actions.organizationCountCards(profile?.organization_id);
    }
  }, [profile]);
  return [isLoading, value[name]];
};

export const useOrganizationTransactionHistogram = (params) => {
  const [profile] = useGetProfile();
  const isLoading = useApiLoading(c.ORGANIZATION_TRANSACTION, 'get');
  const data = useApiGet(c.ORGANIZATION_TRANSACTION, []);
  React.useEffect(() => {
    if (profile?.organization_id) {
      actions.getOrganizationTransactionHistogram(
        profile?.organization_id,
        params
      );
    }
  }, [profile, params]);
  const { table, chart } = React.useMemo(() => {
    const tableData = [];
    const chartData = [];
    if (data.length !== 0) {
      data?.forEach((x) => {
        tableData.push({
          id: x.id,
          label: x.label,
          amount: x.amount,
          count: x.count,
        });
        chartData.push({
          id: x.id,
          label: x.label,
          count: x.count,
        });
      });
    }
    return { table: tableData, chart: chartData };
  }, [data]);
  return [isLoading, table, chart];
};

export const useOrganizationTopBillers = () => {
  const profile = useApiGet(PROFILE, {});
  const isLoading = useApiLoading(c.ORGANIZATION_TOP_BILLERS, 'get');
  const data = useApiGet(c.ORGANIZATION_TOP_BILLERS, { table: [], chart: [] });
  useEffect(() => {
    if (profile?.organization_id) {
      actions.organizationTopBillers(profile?.organization_id);
    }
  }, [profile]);
  return [isLoading, data?.table, data?.chart, ''];
};

export const useOrganizationTopBillerTypes = () => {
  const profile = useApiGet(PROFILE, {});
  const isLoading = useApiLoading(c.ORGANIZATION_TOP_BILLER_TYPES, 'get');
  const data = useApiGet(c.ORGANIZATION_TOP_BILLER_TYPES, {
    table: [],
    chart: [],
  });
  useEffect(() => {
    if (profile?.organization_id) {
      actions.organizationTopBillerTypes(profile?.organization_id);
    }
  }, [profile]);
  return [isLoading, data?.table, data?.chart, ''];
};

export const useOrganizationComparativeTransactions = (params) => {
  const profile = useApiGet(PROFILE, {});
  const isLoading = useApiLoading(
    c.ORGANIZATION_COMPARATIVE_TRANSACTION,
    'get'
  );
  const data = useApiGet(c.ORGANIZATION_COMPARATIVE_TRANSACTION, {});
  useEffect(() => {
    if (profile?.organization_id && params?.year_start && params?.year_end) {
      actions.organizationComparativeTransaction(
        profile?.organization_id,
        params
      );
    }
  }, [params, profile]);
  return [isLoading, data?.table || [], data?.graph || []];
};
